(() => {
  const config = window.SaltMurphyLowStockConfig || {};
  if (!config.enabled) return;

  document.documentElement.style.setProperty('--sm-low-stock-color', config.lowStockColor || '#ff2b2b');

  const apiVersion = '2026-07';
  const cache = new Map();
  let currentProductHandle = null;
  let currentVariantStates = new Map();

  const normalize = value => String(value || '').trim().toLowerCase();
  const optionKey = selectedOptions => selectedOptions
    .map(option => `${normalize(option.name)}=${normalize(option.value)}`)
    .sort()
    .join('|');

  const query = `
    query SaltMurphyMarketInventory($handle: String!, $country: CountryCode!) @inContext(country: $country) {
      product(handle: $handle) {
        variants(first: 250) {
          nodes {
            id
            availableForSale
            quantityAvailable
            selectedOptions { name value }
          }
        }
      }
    }
  `;

  function numericId(gid) {
    const match = String(gid || '').match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  function stateFromNode(node) {
    const quantity = Number.isFinite(node.quantityAvailable) ? node.quantityAvailable : null;
    const threshold = Math.max(2, Number(config.threshold) || 10);
    return {
      id: numericId(node.id),
      quantity,
      available: Boolean(node.availableForSale),
      lowStock: Boolean(node.availableForSale && quantity !== null && quantity > 0 && quantity < threshold),
      lastOne: Boolean(node.availableForSale && quantity === 1),
      selectedOptions: node.selectedOptions || []
    };
  }

  async function fetchInventory(handle) {
    if (!config.token || !config.shopDomain || !config.country) {
      throw new Error('Storefront public access token is not configured.');
    }

    const cacheKey = `${handle}:${config.country}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const promise = fetch(`https://${config.shopDomain}/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.token
      },
      body: JSON.stringify({ query, variables: { handle, country: config.country } })
    }).then(async response => {
      const payload = await response.json();
      if (!response.ok || payload.errors) {
        throw new Error(payload.errors?.map(error => error.message).join('; ') || `HTTP ${response.status}`);
      }
      return payload.data?.product?.variants?.nodes || [];
    });

    cache.set(cacheKey, promise);
    return promise;
  }

  function selectedOptionsFromPicker(picker) {
    return [...picker.querySelectorAll('input[type="radio"][data-option-name]:checked')].map(input => ({
      name: input.dataset.optionName,
      value: input.dataset.optionValueName
    }));
  }

  function findVariantForOption(states, selectedOptions, optionName, optionValue) {
    const target = selectedOptions
      .filter(option => normalize(option.name) !== normalize(optionName))
      .concat({ name: optionName, value: optionValue });
    const targetKey = optionKey(target);
    return states.find(state => optionKey(state.selectedOptions) === targetKey) || null;
  }

  function updatePicker(picker) {
    const sizeFieldsets = picker.querySelectorAll('[data-sm-size-option]');
    sizeFieldsets.forEach(fieldset => {
      const optionName = fieldset.dataset.optionName;
      const selectedOptions = selectedOptionsFromPicker(picker);

      fieldset.querySelectorAll('input[type="radio"][data-option-value-name]').forEach(input => {
        const label = fieldset.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (!label) return;
        const state = findVariantForOption([...currentVariantStates.values()], selectedOptions, optionName, input.dataset.optionValueName);
        label.classList.toggle('is-low-stock', Boolean(state?.lowStock));
      });

      const selectedInput = fieldset.querySelector('input[type="radio"]:checked');
      const message = fieldset.querySelector('[data-sm-low-stock-message]');
      if (!message || !selectedInput) return;
      const selectedState = findVariantForOption([...currentVariantStates.values()], selectedOptions, optionName, selectedInput.dataset.optionValueName);
      const text = service.messageFor(selectedState);
      message.textContent = text || '';
      message.hidden = !text;
    });
  }

  function updateAllPickers() {
    document.querySelectorAll('variant-picker').forEach(updatePicker);
    document.dispatchEvent(new CustomEvent('sm:market-inventory-updated'));
  }

  const service = {
    async load(handle) {
      if (!handle) return;
      currentProductHandle = handle;
      const nodes = await fetchInventory(handle);
      currentVariantStates = new Map(nodes.map(node => {
        const state = stateFromNode(node);
        return [state.id, state];
      }).filter(([id]) => id));
      updateAllPickers();
      document.dispatchEvent(new CustomEvent('sm:market-inventory-ready', { detail: { handle } }));
      return currentVariantStates;
    },
    getVariantState(id) {
      return currentVariantStates.get(Number(id)) || null;
    },
    messageFor(state) {
      if (!state?.lowStock) return '';
      return state.lastOne ? config.lastOneMessage : config.lowStockMessage;
    },
    refresh() {
      updateAllPickers();
    }
  };

  window.SaltMurphyMarketInventory = service;

  function init() {
    const picker = document.querySelector('variant-picker[handle]');
    const handle = picker?.getAttribute('handle') || document.querySelector('sticky-atc')?.dataset.productHandle;
    if (!handle) return;
    service.load(handle).catch(error => {
      console.warn('[Salt Murphy low stock]', error.message);
    });
  }

  document.addEventListener('change', event => {
    if (event.target.matches('variant-picker input[type="radio"]')) {
      queueMicrotask(updateAllPickers);
    }
  });

  document.addEventListener('variant:change', () => {
    requestAnimationFrame(updateAllPickers);
  });

  document.addEventListener('shopify:section:load', () => {
    if (currentProductHandle) service.refresh();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
